import axios from 'axios';
import { Customer, Vehicle, WorkOrder, Estimate, ServiceTemplate, PartTemplate, LineItemTemplate, PartSavedTemplate, ShopSettings, Template, LaborRate, PartMarkupPreset } from '../types';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';
import { triggerSessionEnd } from '../session/graceful-exit';

// Validate required environment variables
if (!process.env.REACT_APP_API_URL) {
  logger.critical('REACT_APP_API_URL environment variable is not set!');
  throw new Error('Missing required environment variable: REACT_APP_API_URL');
}

const API_BASE_URL = process.env.REACT_APP_API_URL + '/admin';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Add authentication token to all requests
api.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh and 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          // Try to refresh token
          const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`, {
            refreshToken: tokens.refreshToken,
          });

          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Unrecoverable: hand off to the graceful-exit engine (save/stash → calm logout).
        clearTokens();
        if (!triggerSessionEnd() && !window.location.pathname.includes('/login')) {
          window.location.href = '/login'; // no React sink mounted — hard fallback
        }
        return Promise.reject(refreshError);
      }
    }

    // Enhanced error logging for debugging
    if (error.response) {
      logger.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    } else if (error.request) {
      logger.error('API No Response:', {
        url: error.config?.url,
        method: error.config?.method,
      });
    } else {
      logger.error('API Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export const customerApi = {
  getAll: () => api.get<Customer[]>('/customers'),
  getById: (id: number) => api.get<Customer>(`/customers/${id}`),
  create: (customer: Omit<Customer, 'id'>) => api.post<Customer>('/customers', customer),
  update: (id: number, customer: Partial<Customer>) => api.put<Customer>(`/customers/${id}`, customer),
  delete: (id: number) => api.delete(`/customers/${id}`),
};

/**
 * WP-02 — customer/vehicle completeness.
 *
 * The rules themselves live on the SERVER, in exactly one module. This client
 * asks what is required and what a given customer is missing; it does not restate
 * the rules. Any component that hard-codes "an invoice needs a ZIP" reintroduces
 * the duplication WP-02 exists to remove — render `missing[]` instead.
 *
 * Contract: docs/contracts/customer-vehicle-model.md
 */
export type CompletenessStage = 'estimate' | 'work_order' | 'invoice' | 'payment';

export interface CompletenessMissingField {
  entity: 'customer' | 'vehicle';
  field: string;
  /** Human-readable and customer-safe — render this, do not invent a label. */
  label: string;
}

export interface CustomerCompleteness {
  customerId: number;
  vehicleId: number | null;
  stage?: CompletenessStage;
  complete?: boolean;
  missing?: CompletenessMissingField[];
  stages: Record<CompletenessStage, boolean>;
}

export const completenessApi = {
  /** Static per deploy — safe to fetch once per session. */
  getRules: () =>
    api.get<{
      stages: Record<CompletenessStage, { customer: string[]; either: string[][]; vehicle: string[] }>;
    }>('/completeness/rules'),

  /**
   * Omit `stage` for every stage's verdict in one round trip; pass it to also get
   * the specific `missing[]` list to prompt for.
   */
  forCustomer: (customerId: number, opts: { stage?: CompletenessStage; vehicleId?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.stage) qs.set('stage', opts.stage);
    if (opts.vehicleId != null) qs.set('vehicleId', String(opts.vehicleId));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return api.get<CustomerCompleteness>(`/customers/${customerId}/completeness${suffix}`);
  },
};

export const vehicleApi = {
  getByCustomerId: (customerId: number) => api.get<Vehicle[]>(`/customers/${customerId}/vehicles`),
  getById: (id: number) => api.get<Vehicle>(`/vehicles/${id}`),
  create: (vehicle: Omit<Vehicle, 'id'>) => api.post<Vehicle>('/vehicles', vehicle),
  update: (id: number, vehicle: Partial<Vehicle>) => api.put<Vehicle>(`/vehicles/${id}`, vehicle),
  delete: (id: number) => api.delete(`/vehicles/${id}`),
  vinDecode: (id: number) => api.post<{ decoded: import('../types').VinDecodeResult; vehicle: Vehicle }>(`/vehicles/${id}/vin-decode`),
  vinLookup: (vin: string) => api.post<{ decoded: import('../types').VinDecodeResult }>('/vehicles/vin-lookup', { vin }),
};

export const workOrderApi = {
  getAll: (params?: { status?: string; limit?: number; sort?: string; search?: string; customer_id?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.sort) qs.set('sort', params.sort);
    if (params?.search) qs.set('search', params.search);
    if (params?.customer_id) qs.set('customer_id', String(params.customer_id));
    const q = qs.toString();
    return api.get<WorkOrder[]>(`/work-orders${q ? `?${q}` : ''}`);
  },
  getById: (id: number) => api.get<WorkOrder>(`/work-orders/${id}`),
  create: (workOrder: Omit<WorkOrder, 'id'>) => api.post<WorkOrder>('/work-orders', workOrder),
  update: (id: number, workOrder: Partial<WorkOrder>) => api.put<WorkOrder>(`/work-orders/${id}`, workOrder),
  updateStatus: (
    id: number,
    status: WorkOrder['status'],
    actualHoursEntries?: Array<{ line_item_id: number; actual_hours: number }>,
  ) => {
    const body: Record<string, unknown> = { status };
    if (actualHoursEntries && actualHoursEntries.length > 0) {
      body.actual_hours_entries = actualHoursEntries;
    }
    return api.patch<WorkOrder>(`/work-orders/${id}/status`, body);
  },
  updatePartsStatus: (id: number, payload: { parts_eta?: string | null; parts_supplier?: string | null }) =>
    api.patch<WorkOrder>(`/work-orders/${id}/parts-status`, payload),
  delete: (id: number) => api.delete(`/work-orders/${id}`),
  getArchived: (search?: string) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    const q = qs.toString();
    return api.get<WorkOrder[]>(`/work-orders/archived${q ? `?${q}` : ''}`);
  },
  unarchive: (id: number) => api.patch<WorkOrder>(`/work-orders/${id}/unarchive`),
  togglePublicLink: (id: number, active: boolean) => api.put(`/work-orders/${id}`, { public_link_active: active }),
  // Promo codes. The workorders router is mounted at both /api/admin/work-orders
  // and /api/work-orders, so the `/admin`-based `api` instance resolves correctly.
  applyPromo: (id: number, code: string) => api.post<WorkOrder>(`/work-orders/${id}/apply-promo`, { code }),
  removePromo: (id: number) => api.delete<WorkOrder>(`/work-orders/${id}/apply-promo`),
};

export interface SendEstimatePayload {
  method: 'email' | 'sms';
  to?: string;
  shopName?: string;
  shopPhone?: string;
}

export interface SendEstimateResponse {
  success: boolean;
  publicToken: string;
  publicUrl: string;
}

export const estimateApi = {
  getAll: (params?: { search?: string; status?: string; limit?: number; sort?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.sort) searchParams.append('sort', params.sort);
    const queryString = searchParams.toString();
    return api.get<Estimate[]>(`/estimates${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => api.get<Estimate>(`/estimates/${id}`),
  create: (estimate: Omit<Estimate, 'id'>) => api.post<Estimate>('/estimates', estimate),
  update: (id: number, estimate: Partial<Estimate>) => api.put<Estimate>(`/estimates/${id}`, estimate),
  updateStatus: (id: number, status: Estimate['status']) => api.patch<Estimate>(`/estimates/${id}/status`, { status }),
  convertToWorkOrder: (id: number) => api.post<WorkOrder>(`/estimates/${id}/convert`),
  send: (id: number, payload: SendEstimatePayload) =>
    api.post<SendEstimateResponse>(`/estimates/${id}/send`, payload),
  delete: (id: number, force?: boolean) => {
    const queryString = force ? '?force=true' : '';
    return api.delete(`/estimates/${id}${queryString}`);
  },
  saveDraft: (data: Record<string, unknown>) =>
    api.post<Estimate>('/estimates/draft', data),
  clone: (id: number) => api.post(`/estimates/${id}/clone`),
  togglePublicLink: (id: number, active: boolean) => api.put(`/estimates/${id}`, { public_link_active: active }),
  // Promo codes. The estimates router is mounted at both /api/admin/estimates and
  // /api/estimates, so the `/admin`-based `api` instance resolves correctly.
  applyPromo: (id: number, code: string) => api.post<Estimate>(`/estimates/${id}/apply-promo`, { code }),
  removePromo: (id: number) => api.delete<Estimate>(`/estimates/${id}/apply-promo`),
};

export const serviceTemplateApi = {
  getAll: (params?: { search?: string; category?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    const queryString = searchParams.toString();
    return api.get<ServiceTemplate[]>(`/templates/services${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: string) => api.get<ServiceTemplate>(`/templates/services/${id}`),
  create: (template: ServiceTemplate) => api.post<ServiceTemplate>('/templates/services', template),
  update: (id: string, template: Partial<ServiceTemplate>) => api.put<ServiceTemplate>(`/templates/services/${id}`, template),
  delete: (id: string) => api.delete(`/templates/services/${id}`),
  getCategories: () => api.get<string[]>('/templates/services/categories'),
};

export const partTemplateApi = {
  getAll: (params?: { search?: string; category?: string; availability?: string; supplier?: string; brand?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.availability) searchParams.append('availability', params.availability);
    if (params?.supplier) searchParams.append('supplier', params.supplier);
    if (params?.brand) searchParams.append('brand', params.brand);
    const queryString = searchParams.toString();
    return api.get<PartTemplate[]>(`/templates/parts${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: string) => api.get<PartTemplate>(`/templates/parts/${id}`),
  getByPartNumber: (partNumber: string) => api.get<PartTemplate>(`/templates/parts/part-number/${partNumber}`),
  create: (template: PartTemplate) => api.post<PartTemplate>('/templates/parts', template),
  update: (id: string, template: Partial<PartTemplate>) => api.put<PartTemplate>(`/templates/parts/${id}`, template),
  delete: (id: string) => api.delete(`/templates/parts/${id}`),
  getCategories: () => api.get<string[]>('/templates/parts/categories'),
  getSuppliers: () => api.get<string[]>('/templates/parts/suppliers'),
  getBrands: () => api.get<string[]>('/templates/parts/brands'),
};

export const lineItemTemplateApi = {
  getAll: (params?: { search?: string; category?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    const queryString = searchParams.toString();
    return api.get<LineItemTemplate[]>(`/templates/line-items${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => api.get<LineItemTemplate>(`/templates/line-items/${id}`),
  create: (template: Omit<LineItemTemplate, 'id'>) => api.post<LineItemTemplate>('/templates/line-items', template),
  update: (id: number, template: Partial<LineItemTemplate>) => api.put<LineItemTemplate>(`/templates/line-items/${id}`, template),
  delete: (id: number) => api.delete(`/templates/line-items/${id}`),
  getCategories: () => api.get<string[]>('/templates/line-items/categories'),
};

export const partSavedTemplateApi = {
  getAll: (params?: { search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    const queryString = searchParams.toString();
    return api.get<PartSavedTemplate[]>(`/templates/parts-saved${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => api.get<PartSavedTemplate>(`/templates/parts-saved/${id}`),
  create: (template: Omit<PartSavedTemplate, 'id'>) => api.post<PartSavedTemplate>('/templates/parts-saved', template),
  update: (id: number, template: Partial<PartSavedTemplate>) => api.put<PartSavedTemplate>(`/templates/parts-saved/${id}`, template),
  delete: (id: number) => api.delete(`/templates/parts-saved/${id}`),
};

export const templateApi = {
  getAll: (params?: { type?: 'line' | 'part'; search?: string }) => {
    const sp = new URLSearchParams();
    if (params?.type) sp.append('type', params.type);
    if (params?.search) sp.append('search', params.search);
    const qs = sp.toString();
    return api.get<Template[]>(`/templates/unified${qs ? `?${qs}` : ''}`);
  },
  getById: (id: number) => api.get<Template>(`/templates/unified/${id}`),
  create: (t: Omit<Template, 'id' | 'usage_count'>) => api.post<Template>('/templates/unified', t),
  update: (id: number, t: Partial<Template>) => api.put<Template>(`/templates/unified/${id}`, t),
  delete: (id: number) => api.delete(`/templates/unified/${id}`),
  use: (id: number) => api.post(`/templates/unified/${id}/use`).then(() => undefined),
  /**
   * WP-09 — owner-only. Put a labor template into the inspection quick picker,
   * or take it out. Returns the saved row so the caller shows what persisted
   * rather than what it hoped for.
   */
  setQuickPickerEligible: (id: number, eligible: boolean) =>
    api.patch<Template>(`/templates/unified/${id}/quick-picker`, { eligible }),
};

export const shopSettingsApi = {
  get: () => api.get<ShopSettings>('/shop-settings'),
  update: (settings: Partial<ShopSettings>) => api.put<ShopSettings>('/shop-settings', settings),
};

export const notificationSettingsApi = {
  get: () => api.get('/notification-settings'),
  update: (settings: any) => api.put('/notification-settings', settings),
};

// Note: Payment API calls are handled directly through paymentService
// which uses /api/admin/payments as its base URL

// Operation Seeder API
// Base URL is already REACT_APP_API_URL + '/admin', so /seeder/... resolves to /api/admin/seeder/...
export const operationSeederApi = {
  runJob: (params: { vehicleId?: number; category?: string; targetCount: number }) =>
    api.post<{ jobId: number; message: string }>('/seeder/run', params),

  getJobs: () =>
    api.get<Array<{
      id: number;
      status: string;
      vehicle_id: number | null;
      category: string | null;
      target_count: number;
      generated: number;
      failed: number;
      csv_path: string | null;
      created_at: string;
      completed_at: string | null;
    }>>('/seeder/jobs'),

  getJobStatus: (id: number) =>
    api.get<{
      id: number;
      status: string;
      generated: number;
      failed: number;
      target_count: number;
      csv_path: string | null;
      completed_at: string | null;
    }>(`/seeder/jobs/${id}`),

  downloadCsv: (jobId: number) =>
    api.get(`/seeder/download/${jobId}`, { responseType: 'blob' as const }),

  importCsv: (formData: FormData) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      '/seeder/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
};

export const laborRateApi = {
  getAll: () => api.get<LaborRate[]>('/labor-rates'),
  create: (r: { name: string; hourly_rate: number; is_default?: boolean }) => api.post<LaborRate>('/labor-rates', r),
  update: (id: number, r: Partial<{ name: string; hourly_rate: number; is_default: boolean }>) => api.put<LaborRate>(`/labor-rates/${id}`, r),
  delete: (id: number) => api.delete(`/labor-rates/${id}`),
  setDefault: (id: number) => api.post<LaborRate>(`/labor-rates/${id}/default`),
  openUsage: (id: number) => api.get<{ count: number }>(`/labor-rates/${id}/open-usage`),
  applyToOpen: (id: number, hourly_rate: number) => api.post<{ updated: number }>(`/labor-rates/${id}/apply-to-open`, { hourly_rate }),
};

export const partMarkupApi = {
  getAll: () => api.get<PartMarkupPreset[]>('/part-markup-presets'),
  create: (p: { name: string; markup_percentage: number; is_default?: boolean }) => api.post<PartMarkupPreset>('/part-markup-presets', p),
  update: (id: number, p: Partial<{ name: string; markup_percentage: number; is_default: boolean }>) => api.put<PartMarkupPreset>(`/part-markup-presets/${id}`, p),
  delete: (id: number) => api.delete(`/part-markup-presets/${id}`),
  setDefault: (id: number) => api.post<PartMarkupPreset>(`/part-markup-presets/${id}/default`),
};

// ─── Procedures (Knowledge Base) ──────────────────────────────────────────

export interface LaborProcedure {
  id: number;
  operation_key: string;
  category: string;
  service_description: string;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_year_from: number | null;
  vehicle_year_to: number | null;
  engine_size: string | null;
  transmission_type: string | null;
  fuel_type: string | null;
  drive_type: string | null;
  steps_json: any[];
  special_tools_json: any[];
  parts_json: any[];
  parts_fit_year_from: number | null;
  parts_fit_year_to: number | null;
  concern_template: string | null;
  cause_template: string | null;
  correction_template: string | null;
  author_role: string | null;
  author_user_id: number | null;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  synthesis_confidence: number | null;
  procedure_sources_json: string | null;
  parts_sources_json: string | null;
  source: string;
  confidence: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProceduresListResponse {
  data: LaborProcedure[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export const proceduresApi = {
  getAll: (params?: {
    category?: string;
    make?: string;
    model?: string;
    year?: number;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.make) searchParams.append('make', params.make);
    if (params?.model) searchParams.append('model', params.model);
    if (params?.year) searchParams.append('year', String(params.year));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.offset) searchParams.append('offset', String(params.offset));
    const qs = searchParams.toString();
    return api.get<ProceduresListResponse>(`/procedures${qs ? `?${qs}` : ''}`);
  },
  getById: (id: number) => api.get<LaborProcedure>(`/procedures/${id}`),
  update: (
    id: number,
    body: {
      steps?: any[];
      special_tools?: any[];
      parts_estimate?: any[];
      concern_template?: string;
      cause_template?: string;
      correction_template?: string;
    },
  ) => api.put<LaborProcedure>(`/procedures/${id}`, body),
  review: (id: number) => api.post<LaborProcedure>(`/procedures/${id}/review`),
  generate: (payload: {
    category: string;
    service_description: string;
    vehicle?: Record<string, unknown>;
    force_regenerate?: boolean;
  }) => api.post(`/procedures/generate`, payload),
};

// ─── Service Categories API ──────────────────────────────────────────────────
// Base URL is already REACT_APP_API_URL + '/admin', so /categories/... resolves to /api/admin/categories/...
export const serviceCategoriesApi = {
  list: (): Promise<{ categories: any[] }> =>
    api.get('/categories').then((r) => r.data),

  getProposals: (): Promise<{ proposals: any[] }> =>
    api.get('/categories/proposals').then((r) => r.data),

  approve: (id: number): Promise<{ success: boolean; category: any }> =>
    api.post(`/categories/proposals/${id}/approve`).then((r) => r.data),

  reject: (id: number): Promise<{ success: boolean }> =>
    api.post(`/categories/proposals/${id}/reject`).then((r) => r.data),

  create: (data: {
    slug: string;
    display_name: string;
    description?: string;
    icon?: string;
    keywords?: string[];
    default_labor_rate?: number;
    sort_order?: number;
  }): Promise<{ category: any }> =>
    api.post('/categories', data).then((r) => r.data),

  update: (slug: string, data: {
    display_name?: string;
    description?: string;
    icon?: string;
    keywords?: string[];
    default_labor_rate?: number;
    sort_order?: number;
  }): Promise<{ category: any }> =>
    api.put(`/categories/${slug}`, data).then((r) => r.data),

  deactivate: (slug: string): Promise<{ success: boolean }> =>
    api.delete(`/categories/${slug}`).then((r) => r.data),
};

// Attachment API
export const attachmentApi = {
  upload: (formData: FormData) =>
    api.post('/attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  saveLink: (data: { entity_type: string; entity_id: number; file_url: string; file_name?: string; notes?: string }) =>
    api.post('/attachments', data),
  getByEntity: (entityType: string, entityId: number) =>
    api.get(`/attachments/${entityType}/${entityId}`),
  updateVisibility: (id: number, visibility: 'internal' | 'customer') =>
    api.put(`/attachments/${id}/visibility`, { visibility }),
  bulkSetInternal: (entityType: string, entityId: number) =>
    api.post(`/attachments/${entityType}/${entityId}/bulk-internal`),
  delete: (id: number) =>
    api.delete(`/attachments/${id}`),
};

// Named export alongside the default, so modules that want to `vi.mock`
// this file (auto-mocking every export) can still assert directly against
// `api.get`/`api.put` — see ReceiptSettings.tsx and its test.
export { api };
export default api;